import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ButtonDirective } from '@coreui/angular-pro';
import { I18nService, TranslatePipe } from 'i18n';

import { FunnelService, QuizSession } from '../../../services/funnel.service';
import { FunnelShellComponent } from '../funnel-shell/funnel-shell.component';

/**
 * Final funnel page. By the time the user lands here:
 *   * their order is `completed` server-side
 *   * Order#after_commit has flipped FunnelQuizSession to `purchased`
 *   * Order#after_commit has sent a password-reset email so they can set a password
 *
 * We show the confirmation and a CTA:
 *   1. "Set your password" — resends the password-setup link to the funnel user
 *      directly (no need to retype their email) and confirms inline. Falls back
 *      to the /:lang/forgot-password page if we can't resolve the session.
 *
 * KYC is no longer performed in-app — identity verification is handled by the
 * card issuer as part of the card creation process.
 */
@Component({
  selector: 'app-funnel-success-page',
  standalone: true,
  imports: [CommonModule, ButtonDirective, TranslatePipe, FunnelShellComponent],
  templateUrl: './funnel-success-page.component.html',
  styleUrls: ['./funnel-success-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FunnelSuccessPageComponent {
  private router     = inject(Router);
  private funnel     = inject(FunnelService);
  private i18n       = inject(I18nService);
  private destroyRef = inject(DestroyRef);

  loading = signal(true);
  productCode = signal<string | null>(null);

  // "Set your password" — resend the login link to the funnel user.
  pwBusy  = signal(false);
  pwSent  = signal<string | null>(null);   // holds the email it was sent to
  pwError = signal(false);

  productI18nKey = computed(() => {
    const c = this.productCode();
    return c ? `funnel.result.${c.toLowerCase()}.title` : null;
  });

  constructor() {
    // Works without a local token too — the backend resumes via the `cid`
    // auth cookie; on any error we simply show the page without product info.
    this.funnel.showQuiz()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (session: QuizSession) => {
          this.loading.set(false);
          this.productCode.set(session.recommended_product_code || null);
        },
        error: () => this.loading.set(false)
      });
  }

  goToPasswordSetup(): void {
    if (this.pwBusy()) return;

    this.pwBusy.set(true);
    this.pwError.set(false);

    this.funnel.requestPasswordSetup()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.pwBusy.set(false);
          this.pwSent.set(res.email || '');
        },
        error: () => {
          // Couldn't resolve the funnel session — fall back to the page where
          // the user can re-request the link by typing their email.
          this.pwBusy.set(false);
          const lang = this.i18n.getLanguage() || 'en';
          this.router.navigateByUrl(`/${lang}/forgot-password`);
        }
      });
  }
}
